import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Check, ArrowLeft, CreditCard, Zap, Sparkles, Building2, Calculator, TrendingDown, DollarSign } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useTheme } from "@/contexts/ThemeContext";
import ThemeToggle from "@/components/ThemeToggle";
import PageTransition from "@/components/PageTransition";
import AnimatedCounter from "@/components/AnimatedCounter";
import { staggerContainer, staggerItem, hoverScale, fadeInUp } from "@/config/animations";

import { PRICING_PLANS } from "@/config/pricing";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_STACKED = "/logo_dark.png";
const LOGO_LIGHT = "/logo_light.png";
const LOGO_ICON = "https://customer-assets.emergentagent.com/job_d04ef3c1-d7de-40d8-b6b3-0d99244d2801/artifacts/0hpd8r65_icon_markremoved%20backround.png";

const PRICING_ICONS = {
  starter: <Zap className="w-6 h-6" />,
  team: <Sparkles className="w-6 h-6" />,
  enterprise: <Building2 className="w-6 h-6" />
};

const PRICING_FEATURES = {
  free: PRICING_PLANS.free.features,
  starter: PRICING_PLANS.starter.features,
  team: PRICING_PLANS.team.features,
  enterprise: PRICING_PLANS.enterprise.features,
};

export default function Billing() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { isDark } = useTheme();
  const [subscription, setSubscription] = useState(null);
  const [packages, setPackages] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSubscription();
    fetchPackages();
  }, []);

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/billing/subscription`);
      setSubscription(response.data);
    } catch (error) {
      console.error("Failed to fetch subscription:", error);
    }
  };

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/billing/packages`);
      setPackages(response.data);
    } catch (error) {
      console.error("Failed to fetch packages:", error);
    }
  };

  const handleUpgrade = async (packageId) => {
    if (packageId === "starter") {
      toast.info("You're already on the Starter plan!");
      return;
    }

    setLoading(true);

    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/billing/checkout`, {
        package_id: packageId,
        origin_url: originUrl
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to create checkout session";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const usagePercent = subscription 
    ? (subscription.optimizations_used / (typeof subscription.optimizations_limit === 'number' ? subscription.optimizations_limit : 10000)) * 100
    : 0;

  return (
    <PageTransition>
      <div className="min-h-screen bg-[var(--qn-background)]">
        {/* Header */}
        <motion.header 
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="glass border-b border-[var(--qn-border)]"
        >
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <motion.div {...hoverScale}>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => navigate("/dashboard")}
                  data-testid="back-btn" data-automation-id="back-btn"
                >
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </motion.div>
              <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => navigate("/")}>
                <img src={LOGO_ICON} alt="QNNEAL" className="h-10 w-10 object-contain" />
                <span className="font-outfit font-bold text-2xl tracking-tight">QNNEAL</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <ThemeToggle />
              <h1 className="font-outfit font-semibold text-xl">Billing & Subscription</h1>
            </div>
          </div>
        </motion.header>

        <main className="max-w-7xl mx-auto px-6 py-12">
          {/* Current Plan */}
          {subscription && (
            <motion.div
              initial={fadeInUp.initial}
              animate={fadeInUp.animate}
              transition={fadeInUp.transition}
            >
              <Card className="bg-[var(--qn-card)] border-[var(--qn-border)] mb-12">
                <CardHeader>
                  <CardTitle className="font-outfit flex items-center gap-3">
                    <CreditCard className="w-5 h-5 text-[var(--qn-primary)]" />
                    Current Plan
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-2xl font-outfit font-bold">{subscription.name}</span>
                        {subscription.tier !== "starter" && (
                          <Badge className="bg-[var(--qn-primary-muted)] text-[var(--qn-primary)] border-[var(--qn-primary)]/30">Active</Badge>
                        )}
                      </div>
                      <div className="text-[var(--qn-foreground-muted)]">
                        {subscription.price === 0 ? "Free" : `$${subscription.price}/month`}
                      </div>
                    </div>

                    <div className="flex-1 max-w-md">
                      <div className="flex justify-between text-base text-[var(--qn-foreground-subtle)] mb-2">
                        <span>Optimizations used</span>
                        <span className="font-mono">
                          <AnimatedCounter value={subscription.optimizations_used} /> / {subscription.optimizations_limit}
                        </span>
                      </div>
                      <Progress value={Math.min(usagePercent, 100)} className="h-3" />
                      {usagePercent >= 80 && (
                        <motion.p 
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="text-amber-400 text-base mt-2"
                        >
                          You're running low on optimizations. Consider upgrading!
                        </motion.p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Pricing Cards */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-center mb-12"
          >
            <h2 className="font-outfit font-bold text-3xl mb-4">You only pay when you save.</h2>
            <p className="text-[var(--qn-foreground-muted)] text-lg max-w-xl mx-auto">
              QNNEAL takes just 10% of your savings. You keep the other 90%.
              If we don't save you money, you don't pay.
            </p>
          </motion.div>

          {/* Example ROI */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="max-w-2xl mx-auto mb-12"
          >
            <Card className="card-themed border-[var(--qn-primary)]/20">
              <CardContent className="p-6 text-center">
                <p className="text-sm text-[var(--qn-foreground-muted)] mb-3">Example: If your agents cost $3,000/mo and we save you 40%:</p>
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <div className="text-xs text-[var(--qn-foreground-subtle)]">Your Savings</div>
                    <div className="text-lg font-mono font-bold text-[var(--qn-success)]">$1,200/mo</div>
                  </div>
                  <div>
                    <div className="text-xs text-[var(--qn-foreground-subtle)]">QNNEAL Fee</div>
                    <div className="text-lg font-mono font-bold">$120/mo</div>
                  </div>
                  <div>
                    <div className="text-xs text-[var(--qn-foreground-subtle)]">You Keep</div>
                    <div className="text-lg font-mono font-bold text-[var(--qn-success)]">$1,080/mo</div>
                  </div>
                  <div>
                    <div className="text-xs text-[var(--qn-foreground-subtle)]">Your ROI</div>
                    <div className="text-lg font-mono font-bold text-[var(--qn-primary)]">900%</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div 
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid lg:grid-cols-3 gap-8 max-w-5xl mx-auto"
          >
            {Object.entries(packages).map(([key, pkg]) => {
              const isCurrentPlan = subscription?.tier === key;
              const isFeatured = key === "team";

              return (
                <motion.div
                  key={key}
                  variants={staggerItem}
                  whileHover={{ scale: 1.03 }}
                >
                  <Card className={`bg-[var(--qn-card)] border-[var(--qn-border)] h-full ${isFeatured ? 'border-[var(--qn-primary)]/50 ring-1 ring-[var(--qn-primary)]/20' : ''}`}>
                    <CardHeader>
                      {isFeatured && (
                        <Badge className="w-fit mb-2 bg-[var(--qn-primary-muted)] text-[var(--qn-primary)] border-[var(--qn-primary)]/30">
                          Most Popular
                        </Badge>
                      )}
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          key === 'free' || key === 'starter' ? 'bg-[var(--qn-surface)] text-[var(--qn-foreground-subtle)]' :
                          key === 'team' ? 'bg-[var(--qn-primary-muted)] text-[var(--qn-primary)]' :
                          'bg-[var(--qn-secondary-muted)] text-[var(--qn-secondary)]'
                        }`}>
                          {PRICING_ICONS[key]}
                        </div>
                        <CardTitle className="font-outfit text-2xl">{pkg.name}</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div>
                        <div className="flex items-baseline gap-1">
                          <span className="metric-value text-4xl">
                            {pkg.custom_pricing ? "Custom" : pkg.price === 0 ? "$0" : `$${pkg.price.toLocaleString()}`}
                          </span>
                          <span className="text-[var(--qn-foreground-subtle)]">
                            {pkg.custom_pricing ? "" : pkg.price === 0 ? "/forever" : "/month"}
                          </span>
                        </div>
                        {pkg.price > 0 && (
                          <div className="text-sm text-[var(--qn-foreground-muted)] mt-1">
                            + {key === "enterprise" ? "8%" : "10%"} of savings
                          </div>
                        )}
                      </div>

                      <motion.div {...hoverScale}>
                        <Button 
                          onClick={() => handleUpgrade(key)}
                          disabled={isCurrentPlan || loading}
                          className={`w-full ${isFeatured ? 'btn-primary' : 'btn-secondary'}`}
                          data-testid={`upgrade-${key}-btn`}
                          data-automation-id={`billing-${key}-btn`}
                        >
                          {isCurrentPlan ? "Current Plan" : 
                           key === "free" ? "Downgrade" :
                           key === "enterprise" ? "Contact Sales" : "Upgrade"}
                        </Button>
                      </motion.div>

                      <ul className="space-y-3">
                        {PRICING_FEATURES[key].map((feature, i) => (
                          <motion.li 
                            key={i}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.3 + i * 0.05 }}
                            className="flex items-start gap-3 text-sm text-[var(--qn-foreground-muted)]"
                          >
                            <Check className="w-4 h-4 text-[var(--qn-success)] mt-0.5 flex-shrink-0" />
                            {feature}
                          </motion.li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Payment Methods Note */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-center mt-12 space-y-3"
          >
            <p className="text-sm text-[var(--qn-foreground-muted)]">
              All plans include full agentic AI optimization across all connected providers. Quantum computing enhancement is available for Team and Enterprise plans.
            </p>
            <p className="flex items-center justify-center gap-2 text-[var(--qn-foreground-subtle)] text-base">
              <CreditCard className="w-4 h-4" />
              Secure payments powered by Stripe. We accept cards and cryptocurrency.
            </p>
          </motion.div>

          {/* ROI Calculator (Phase 11) */}
          <ROICalculator />

          {/* Enterprise Credit Balance (Phase 11) */}
          <CreditBalanceCard />
        </main>
      </div>
    </PageTransition>
  );
}

// ========== ROI Calculator Component ==========
function ROICalculator() {
  const [monthlySpend, setMonthlySpend] = useState(5000);
  const [savingsRate, setSavingsRate] = useState(35);

  const monthlySavings = monthlySpend * (savingsRate / 100);
  const annualSavings = monthlySavings * 12;
  const qnnealFee = monthlySavings * 0.10;
  const netSavings = monthlySavings - qnnealFee;
  const roi = qnnealFee > 0 ? (netSavings / qnnealFee) * 100 : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.6 }}
      className="mt-12"
    >
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="roi-calculator" data-automation-id="roi-calculator">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-[var(--qn-primary)]" />
            ROI Calculator
          </CardTitle>
          <CardDescription className="text-[var(--qn-foreground-muted)]">
            See how much QNNEAL can save your organization
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-8">
            {/* Input Controls */}
            <div className="space-y-6">
              <div>
                <label className="text-sm font-medium mb-2 block">Monthly LLM Spend</label>
                <div className="flex items-center gap-3">
                  <DollarSign className="w-4 h-4 text-[var(--qn-foreground-subtle)]" />
                  <Input
                    type="number"
                    value={monthlySpend}
                    onChange={(e) => setMonthlySpend(Math.max(0, Number(e.target.value)))}
                    className="bg-[var(--qn-background)] border-[var(--qn-border)] max-w-[200px]"
                    data-testid="roi-spend-input" data-automation-id="roi-spend-input"
                  />
                  <span className="text-sm text-[var(--qn-foreground-muted)]">/month</span>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Expected Savings Rate: <span className="text-[var(--qn-primary)]">{savingsRate}%</span>
                </label>
                <Slider
                  value={[savingsRate]}
                  onValueChange={(v) => setSavingsRate(v[0])}
                  min={10}
                  max={60}
                  step={5}
                  className="w-full"
                  data-testid="roi-savings-slider" data-automation-id="roi-savings-slider"
                />
                <div className="flex justify-between text-xs text-[var(--qn-foreground-subtle)] mt-1">
                  <span>Conservative (10%)</span>
                  <span>Aggressive (60%)</span>
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-[var(--qn-background)] rounded-lg p-4">
                  <div className="text-xs text-[var(--qn-foreground-subtle)]">Monthly Savings</div>
                  <div className="text-xl font-semibold text-[var(--qn-success)]" data-testid="roi-monthly-savings" data-automation-id="roi-monthly-savings">
                    ${monthlySavings.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </div>
                </div>
                <div className="bg-[var(--qn-background)] rounded-lg p-4">
                  <div className="text-xs text-[var(--qn-foreground-subtle)]">Annual Savings</div>
                  <div className="text-xl font-semibold text-[var(--qn-success)]" data-testid="roi-annual-savings" data-automation-id="roi-annual-savings">
                    ${annualSavings.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </div>
                </div>
                <div className="bg-[var(--qn-background)] rounded-lg p-4">
                  <div className="text-xs text-[var(--qn-foreground-subtle)]">QNNEAL Fee (10%)</div>
                  <div className="text-xl font-semibold">${qnnealFee.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                </div>
                <div className="bg-[var(--qn-success)]/5 rounded-lg p-4 border border-[var(--qn-success)]/20">
                  <div className="text-xs text-[var(--qn-foreground-subtle)]">Net Savings</div>
                  <div className="text-xl font-bold text-[var(--qn-success)]" data-testid="roi-net-savings" data-automation-id="roi-net-savings">
                    ${netSavings.toLocaleString(undefined, { maximumFractionDigits: 0 })}/mo
                  </div>
                </div>
              </div>
              <div className="bg-[var(--qn-primary)]/5 rounded-lg p-3 border border-[var(--qn-primary)]/20">
                <div className="flex items-center gap-2 text-sm">
                  <TrendingDown className="w-4 h-4 text-[var(--qn-primary)]" />
                  <span>
                    <strong className="text-[var(--qn-primary)]">{roi.toFixed(0)}% ROI</strong>
                    <span className="text-[var(--qn-foreground-muted)]"> — for every $1 you pay QNNEAL, you save ${(netSavings / Math.max(qnnealFee, 1)).toFixed(0)} net</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

// ========== Credit Balance Component ==========
function CreditBalanceCard() {
  const [credits, setCredits] = useState(null);

  useEffect(() => {
    const fetchCredits = async () => {
      try {
        const res = await axios.get(`${API}/credits/balance`);
        setCredits(res.data);
      } catch (e) {
        // Not provisioned yet, ignore
      }
    };
    fetchCredits();
  }, []);

  if (!credits || credits.balance === 0 && credits.lifetime_purchased === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.7 }}
      className="mt-8"
    >
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="credit-balance-card" data-automation-id="credit-balance-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            Enterprise Credit Balance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-[var(--qn-background)] rounded-lg p-4">
              <div className="text-sm text-[var(--qn-foreground-subtle)]">Current Balance</div>
              <div className="text-2xl font-bold" data-testid="credit-current-balance" data-automation-id="credit-current-balance">${credits.balance.toFixed(2)}</div>
            </div>
            <div className="bg-[var(--qn-background)] rounded-lg p-4">
              <div className="text-sm text-[var(--qn-foreground-subtle)]">Lifetime Purchased</div>
              <div className="text-2xl font-bold">${credits.lifetime_purchased.toFixed(2)}</div>
            </div>
            <div className="bg-[var(--qn-background)] rounded-lg p-4">
              <div className="text-sm text-[var(--qn-foreground-subtle)]">Lifetime Used</div>
              <div className="text-2xl font-bold">${credits.lifetime_used.toFixed(2)}</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
